import http from '@utils/http';

/**
 * Path segment for QA submit. Resolved URL: `{ENV.API_URL}/projects/:projectId/qa/submit`
 * (with default tenant API, that is `POST /api/v1/projects/:projectId/qa/submit`).
 * @param {string} projectId
 */
const projectQaSubmitPath = (projectId) => `/projects/${projectId}/qa/submit`;

/**
 * GET /api/v1/projects
 * @param {{
 *   page?: number,
 *   limit?: number,
 *   status?: 'active' | 'inactive',
 *   priority?: 'high' | 'medium' | 'low',
 *   search?: string,
 * }} params - `search` matches project name (backend); `status` / `priority` filter the list.
 */
export const getProjects = (params = {}) =>
    http.get('/projects', { params }).then((res) => {
        const raw = res.data ?? {};
        const list = Array.isArray(raw.data) ? raw.data : [];
        return {
            data: list,
            total: raw.total ?? 0,
            page: raw.page ?? 1,
            limit: raw.limit ?? 10,
            totalPages: raw.totalPages ?? 1,
        };
    });

/**
 * PATCH /api/v1/projects/starred-order
 * @param {string[]} projectIds
 */
export const reorderStarredProjects = (projectIds) =>
    http.patch('/projects/starred-order', { projectIds }).then((res) => res.data?.data ?? res.data);

/**
 * PATCH /api/v1/projects/reorder
 * @param {string[]} projectIds
 */
export const reorderProjects = (projectIds) =>
    http.patch('/projects/reorder', { projectIds }).then((res) => res.data?.data ?? res.data);

/**
 * GET /api/v1/projects/:id
 */
export const getProjectById = (id) => http.get(`/projects/${id}`).then((res) => res.data?.data ?? res.data);

/**
 * GET /api/v1/projects/:id/overview
 */
export const getProjectOverview = (id) =>
    http.get(`/projects/${id}/overview`).then((res) => res.data?.data ?? res.data);

/**
 * GET /api/v1/projects/:id/overview/tasks
 */
export const getProjectOverviewTasks = (id, page = 1, limit = 10) =>
    http.get(`/projects/${id}/overview/tasks`, { params: { page, limit } }).then((res) => res.data?.data ?? res.data);


/**
 * GET /api/v1/projects/:id/summary-analytics
 */
export const getProjectSummaryAnalytics = (id) =>
    http.get(`/projects/${id}/summary-analytics`).then((res) => res.data?.data ?? res.data);

/**
 * GET /api/v1/projects/:id/team-activity
 */
export const getProjectTeamActivity = (id, params = {}) =>
    http.get(`/projects/${id}/team-activity`, { params }).then((res) => {
        const raw = res.data ?? {};
        const list = Array.isArray(raw.data) ? raw.data : [];
        return {
            items: list,
            total: raw.total ?? 0,
            page: raw.page ?? 1,
            limit: raw.limit ?? 15,
            totalPages: raw.totalPages ?? 1,
        };
    });


/**
 * PATCH /api/v1/projects/:id
 * @param {string} id - Project ID
 * @param {{ projectDetails?: { content?: string, attachments?: Array<{ url: string, title?: string }> } }} body
 */
export const updateProjectDetails = (id, body) =>
    http.patch(`/projects/${id}`, body).then((res) => res.data?.data ?? res.data);

/**
 * PATCH /api/v1/projects/:projectId/details
 * Save project description/details. Use multipart when attachments (links or files) are present.
 * @param {string} projectId - Project ID
 * @param {{ content?: string | null, attachmentUrls?: Array<{ type: 'link', url: string, title?: string }>, files?: File[] } | FormData} body
 *   - JSON: { content } when no attachments.
 *   - FormData (multipart): content (markdown/html), attachmentUrls (JSON string), files (File[]) when attaching.
 */
export const updateProjectDetailsByEndpoint = (projectId, body) => {
    if (body instanceof FormData) {
        return http.patch(`/projects/${projectId}/details`, body).then((res) => res.data?.data ?? res.data);
    }
    const files = Array.isArray(body?.files) ? body.files : [];
    const attachments = Array.isArray(body?.attachments) 
        ? body.attachments 
        : Array.isArray(body?.attachmentUrls) 
            ? body.attachmentUrls 
            : [];
    const useMultipart = files.length > 0;
    if (useMultipart) {
        const formData = new FormData();
        formData.append('content', typeof body?.content === 'string' ? body.content : '');
        formData.append('attachmentUrls', JSON.stringify(attachments));
        files.forEach((file) => formData.append('files', file));
        return http.patch(`/projects/${projectId}/details`, formData).then((res) => res.data?.data ?? res.data);
    }
    return http
        .patch(`/projects/${projectId}/details`, { 
            content: body?.content ?? null,
            attachments
        })
        .then((res) => res.data?.data ?? res.data);
};

/**
 * POST /api/v1/projects/:projectId/details/attachments (multipart).
 * @param {string} projectId
 * @param {{ attachmentUrls?: Array<{ type: 'link', url: string, title?: string }>, files?: File[] }} body
 */
export const uploadProjectDetailsAttachments = (projectId, body = {}) => {
    const attachmentUrls = Array.isArray(body.attachmentUrls) ? body.attachmentUrls : [];
    const files = Array.isArray(body.files) ? body.files : [];
    const formData = new FormData();
    formData.append('attachmentUrls', JSON.stringify(attachmentUrls));
    files.forEach((file) => formData.append('files', file));
    return http
        .post(`/projects/${projectId}/details/attachments`, formData)
        .then((res) => res.data?.data ?? res.data);
};

/**
 * DELETE /api/v1/projects/:projectId/details/attachments
 * Directly deletes a project attachment or description attachment.
 * @param {string} projectId
 * @param {{ id?: string, url?: string }} params
 *   - id: deletes from project_attachments table (general/uploaded attachments)
 *   - url: deletes from project_details.attachments JSON array (description attachments/links)
 */
export const deleteProjectDetailsAttachment = (projectId, params) =>
    http
        .delete(`/projects/${projectId}/details/attachments`, { params })
        .then((res) => res.data?.data ?? res.data);


/**
 * GET /api/v1/projects/:id/tabs
 * @returns {{ tabs: Array<{ id: string, label: string, visible: boolean }> }}
 */
export const getProjectTabs = (id) =>
    http.get(`/projects/${id}/tabs`).then((res) => {
        const raw = res.data?.data ?? res.data;
        const list = Array.isArray(raw?.tabs) ? raw.tabs : [];
        return { tabs: list.filter((t) => t.visible !== false) };
    });

/**
 * GET /api/v1/projects/:projectId/client
 * Returns "Client tab" view for a project.
 * @param {string} projectId
 */
export const getProjectClientView = (projectId) =>
    http.get(`/projects/${projectId}/client`).then((res) => res.data?.data ?? res.data);

/**
 * GET /api/v1/projects/:id/milestones
 */
export const getProjectMilestones = (id) =>
    http.get(`/projects/${id}/milestones`).then((res) => {
        const raw = res.data?.data ?? res.data;
        if (Array.isArray(raw)) return raw;
        if (raw && Array.isArray(raw.milestones)) return raw.milestones;
        return [];
    });

/**
 * GET /api/v1/projects/:projectId/phases/:phaseId/milestones
 */
export const getProjectMilestonesForPhase = (projectId, phaseId) =>
    http.get(`/projects/${projectId}/phases/${phaseId}/milestones`).then((res) => {
        const raw = res.data?.data ?? res.data;
        if (Array.isArray(raw)) return raw;
        if (raw && Array.isArray(raw.milestones)) return raw.milestones;
        return [];
    });
/**
 * POST /api/v1/projects/:id/milestones
 * @param {{ name: string, dueDate: string, phaseId?: string | null, description?: object, attachments?: Array<{ url: string, title?: string }> } | FormData} body
 *   - JSON: name, dueDate, phaseId?, description?, attachments? (link-only).
 *   - FormData: when sending files use multipart (name, dueDate, phaseId?, description, attachmentUrls, files) – same as discussion (Multer).
 */
export const createProjectMilestone = (id, body) => {
    if (body instanceof FormData) {
        return http.post(`/projects/${id}/milestones`, body).then((res) => res.data?.data ?? res.data);
    }
    return http.post(`/projects/${id}/milestones`, body).then((res) => res.data?.data ?? res.data);
};

/**
 * PATCH /api/v1/projects/:projectId/milestones/:milestoneId
 * @param {{
 *   name?: string,
 *   dueDate?: string,
 *   description?: object,
 *   isActive?: boolean,
 *   status?: string,
 *   phaseId?: string | null,
 *   attachments?: Array<{ url: string, title?: string }>,
 *   files?: File[],
 * }} body
 */
export const updateProjectMilestone = (projectId, milestoneId, body) =>
    http
        .patch(`/projects/${projectId}/milestones/${milestoneId}`, body)
        .then((res) => res.data?.data ?? res.data);

/**
 * DELETE /api/v1/projects/:projectId/milestones/:milestoneId
 */
export const deleteProjectMilestone = (projectId, milestoneId) =>
    http
        .delete(`/projects/${projectId}/milestones/${milestoneId}`)
        .then((res) => res.data?.data ?? res.data);

/**
 * POST /api/v1/projects/:projectId/milestones/:milestoneId/deliverables
 * @param {{ value: string }} body
 */
export const createMilestoneDeliverable = (projectId, milestoneId, body) =>
    http
        .post(`/projects/${projectId}/milestones/${milestoneId}/deliverables`, body)
        .then((res) => res.data?.data ?? res.data);

/**
 * PATCH /api/v1/projects/:projectId/milestones/:milestoneId/deliverables/:deliverableId
 * @param {{ isMarked?: boolean, value?: string }} body
 */
export const updateMilestoneDeliverable = (projectId, milestoneId, deliverableId, body) =>
    http
        .patch(`/projects/${projectId}/milestones/${milestoneId}/deliverables/${deliverableId}`, body)
        .then((res) => res.data?.data ?? res.data);

/**
 * DELETE /api/v1/projects/:projectId/milestones/:milestoneId/deliverables/:deliverableId
 */
export const deleteMilestoneDeliverable = (projectId, milestoneId, deliverableId) =>
    http
        .delete(`/projects/${projectId}/milestones/${milestoneId}/deliverables/${deliverableId}`)
        .then((res) => res.data?.data ?? res.data);

/**
 * GET /api/v1/projects/:projectId/phases
 * @param {string} projectId
 */
export const getProjectPhases = (projectId) =>
    http.get(`/projects/${projectId}/phases`).then((res) => {
        const payload = res.data ?? {};
        const raw = payload.data ?? payload;
        if (Array.isArray(raw)) return raw;
        if (raw && Array.isArray(raw.phases)) return raw.phases;
        return [];
    });

/**
 * POST /api/v1/projects/:projectId/phases
 * @param {{ title: string, description?: string } | FormData} body
 */
export const createProjectPhase = (projectId, body) => {
    if (body instanceof FormData) {
        return http.post(`/projects/${projectId}/phases`, body).then((res) => res.data?.data ?? res.data);
    }
    return http.post(`/projects/${projectId}/phases`, body).then((res) => res.data?.data ?? res.data);
};

/**
 * PATCH /api/v1/projects/:projectId/phases/:phaseId
 * @param {{ title?: string, description?: string }} body — UI update sends title/description (multipart when files/links).
 */
export const updateProjectPhase = (projectId, phaseId, body) =>
    http.patch(`/projects/${projectId}/phases/${phaseId}`, body).then((res) => res.data?.data ?? res.data);

/**
 * DELETE /api/v1/projects/:projectId/phases/:phaseId
 */
export const deleteProjectPhase = (projectId, phaseId) =>
    http.delete(`/projects/${projectId}/phases/${phaseId}`).then((res) => res.data?.data ?? res.data);

/**
 * POST /api/v1/projects
 * @param {{
 *   name: string,
 *   budget?: number,
 *   priority: string,
 *   clientType: 'mine and platform client'|'platform client'|'out of the platform'|'own product',
 *   clientId?: string,
 *   clientEmail?: string,
 *   clientName?: string,
 *   clientPhone?: string,
 * }} body — `clientType` is required. `budget` is optional in the UI; omitted or invalid values are sent as `0`. With a platform directory client send `clientId`. With a platform client by email send `clientEmail`. External clients send `clientName`, `clientEmail`, `clientPhone`. For tenant-as-client (own product) send only `clientType: 'own product'` plus core fields.
 */
/**
 * GET /api/v1/project-templates
 * Returns list of project templates available for the tenant.
 * @returns {Promise<Array<{ id: string, name: string, description?: string, projectType?: string }>>}
 */
export const getProjectTemplates = () =>
    http.get('/project-templates').then((res) => {
        const raw = res.data ?? {};
        if (Array.isArray(raw)) return raw;
        if (Array.isArray(raw.data)) return raw.data;
        if (Array.isArray(raw.templates)) return raw.templates;
        return [];
    });

/**
 * GET /api/v1/public/categories-and-tags?categories=true
 */
export const getPublicCategories = () =>
    http.get('/public/categories-and-tags', { params: { categories: true } }).then((res) => {
        const categories = res.data?.data?.categories || [];
        return categories.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    });

export const createProject = (body) => http.post('/projects', body).then((res) => res.data?.data ?? res.data);

/**
 * GET /api/v1/projects/:id/sprints
 */
export const getProjectSprints = async (id) => {
    let allSprints = [];
    let page = 1;
    let totalPages = 1;
    const limit = 100;

    try {
        do {
            const res = await http.get(`/projects/${id}/sprints`, { params: { page, limit } });
            const payload = res.data ?? {};
            const raw = payload.data ?? payload;

            let items = [];
            if (Array.isArray(raw)) {
                items = raw;
                totalPages = 1;
            } else if (raw && Array.isArray(raw.items)) {
                items = raw.items;
                totalPages = raw.totalPages ?? 1;
            } else if (raw && Array.isArray(raw.sprints)) {
                items = raw.sprints;
                totalPages = raw.totalPages ?? 1;
            } else {
                break;
            }

            allSprints = allSprints.concat(items);
            if (items.length < limit || page >= totalPages) {
                break;
            }
            page++;
        } while (page <= totalPages);
    } catch (error) {
        console.error('Error fetching sprints:', error);
    }

    return allSprints;
};

/**
 * GET /api/v1/projects/:projectId/phases/:phaseId/sprints
 */
export const getProjectSprintsForPhase = async (projectId, phaseId) => {
    try {
        const res = await http.get(`/projects/${projectId}/phases/${phaseId}/sprints`);
        const payload = res.data ?? {};
        const raw = payload.data ?? payload;
        if (Array.isArray(raw)) return raw;
        if (raw && raw.sprints) {
            if (Array.isArray(raw.sprints)) return raw.sprints;
            if (Array.isArray(raw.sprints.items)) return raw.sprints.items;
        }
        if (raw && Array.isArray(raw.items)) return raw.items;
        return [];
    } catch (error) {
        console.error('Error fetching sprints for phase:', error);
        return [];
    }
};

/**
 * GET /api/v1/projects/:projectId/milestones/:milestoneId/sprints?page=1&limit=10&sortOrder=asc
 */
export const getProjectSprintsForMilestone = async (projectId, milestoneId, page = 1, limit = 10, sortOrder = 'asc') => {
    try {
        const res = await http.get(`/projects/${projectId}/milestones/${milestoneId}/sprints`, {
            params: { page, limit, sortOrder }
        });
        const payload = res.data ?? {};
        const raw = payload.data ?? payload;
        if (Array.isArray(raw)) return raw;
        if (raw && raw.sprints) {
            if (Array.isArray(raw.sprints)) return raw.sprints;
            if (Array.isArray(raw.sprints.items)) return raw.sprints.items;
        }
        if (raw && Array.isArray(raw.items)) return raw.items;
        return [];
    } catch (error) {
        console.error('Error fetching sprints for milestone:', error);
        return [];
    }
};

/**
 * GET /api/v1/projects/:projectId/sprints?page=1&limit=20
 */
export const getProjectSprintsStandalone = async (projectId, page = 1, limit = 20) => {
    try {
        const res = await http.get(`/projects/${projectId}/sprints`, {
            params: { page, limit }
        });
        const payload = res.data ?? {};
        const raw = payload.data ?? payload;
        if (Array.isArray(raw)) return raw;
        if (raw && raw.sprints) {
            if (Array.isArray(raw.sprints)) return raw.sprints;
            if (Array.isArray(raw.sprints.items)) return raw.sprints.items;
        }
        if (raw && Array.isArray(raw.items)) return raw.items;
        return [];
    } catch (error) {
        console.error('Error fetching standalone sprints:', error);
        return [];
    }
};


/**
 * POST /api/v1/projects/:id/sprints
 * @param {{ name: string, milestoneId?: string, startDate: string, endDate: string, description?: object, attachments?: Array<{ url: string, title?: string }> } | FormData} body
 *   - JSON: name, milestoneId, startDate, endDate, description?, attachments?.
 *   - FormData: multipart (same as discussion/Multer) when files attached.
 */
export const createProjectSprint = (id, body) => {
    if (body instanceof FormData) {
        return http.post(`/projects/${id}/sprints`, body).then((res) => res.data?.data ?? res.data);
    }
    return http.post(`/projects/${id}/sprints`, body).then((res) => res.data?.data ?? res.data);
};

/**
 * POST /api/v1/projects/:projectId/task-consultations
 * @param {{ taskIds: string[], consultantIds: string[], qaPassed: boolean, description: object, attachments?: Array<{ type: string, url: string, title?: string }> } | FormData} body
 */
export const createTaskConsultation = (projectId, body) => {
    if (body instanceof FormData) {
        return http.post(`/projects/${projectId}/task-consultations`, body).then((res) => res.data?.data ?? res.data);
    }
    return http.post(`/projects/${projectId}/task-consultations`, body).then((res) => res.data?.data ?? res.data);
};

/**
 * PATCH /api/v1/projects/:projectId/sprints/:sprintId
 * @param {{ name?: string, startDate?: string, endDate?: string, description?: object, attachments?: Array<{ url: string, title?: string }> } | FormData} body
 */
export const updateProjectSprint = (projectId, sprintId, body) => {
    if (body instanceof FormData) {
        return http
            .patch(`/projects/${projectId}/sprints/${sprintId}`, body)
            .then((res) => res.data?.data ?? res.data);
    }
    return http.patch(`/projects/${projectId}/sprints/${sprintId}`, body).then((res) => res.data?.data ?? res.data);
};

/**
 * DELETE /api/v1/projects/:projectId/sprints/:sprintId
 */
export const deleteProjectSprint = (projectId, sprintId) =>
    http.delete(`/projects/${projectId}/sprints/${sprintId}`).then((res) => res.data?.data ?? res.data);

/**
 * GET /api/v1/projects/:projectId/sprints/:sprintId
 * Sprint detail: tasks, submissions, and metadata for one sprint.
 */
export const getProjectSprint = (projectId, sprintId) =>
    http.get(`/projects/${projectId}/sprints/${sprintId}`).then((res) => res.data?.data ?? res.data);

export * from './task/task.service';

/**
 * GET /api/v1/projects/:projectId/bugs
 */
export const getProjectBugs = (projectId) =>
    http.get(`/projects/${projectId}/bugs`).then((res) => {
        const payload = res.data ?? {};
        const raw = payload.data ?? payload;
        if (Array.isArray(raw)) return raw;
        if (raw && Array.isArray(raw.bugs)) return raw.bugs;
        return [];
    });

/**
 * Body for POST /api/v1/projects/:projectId/bugs/batch (multer).
 * Fields: taskId, bugs (JSON array), bugFile_0, bugFile_1, … per bug index.
 * Each bug in JSON: { description: { markdown }, severity, assignedToIds? }. Files are not in JSON.
 * @param {string} taskId
 * @param {Array<{ description: { markdown?: string }, severity: string, assignedToIds?: string[], files?: File[] }>} bugs
 */
export const buildCreateProjectBugsBatchFormData = (taskId, bugs) => {
    const formData = new FormData();
    formData.append('taskId', String(taskId));
    const bugsPayload = bugs.map(({ files: _f, ...rest }) => rest);
    formData.append('bugs', JSON.stringify(bugsPayload));
    bugs.forEach((bug, i) => {
        if (Array.isArray(bug.files) && bug.files.length > 0) {
            bug.files.forEach((file) => {
                formData.append(`bugFile_${i}`, file);
            });
        }
    });
    return formData;
};

/**
 * POST /api/v1/projects/:projectId/bugs/batch
 * Multipart from buildCreateProjectBugsBatchFormData — taskId + bugs JSON + bugFile_*.
 */
export const createProjectBugsBatch = (projectId, formData) =>
    http.post(`/projects/${projectId}/bugs/batch`, formData).then((res) => res.data?.data ?? res.data);

/**
 * POST /api/v1/projects/:projectId/bugs
 * @param {{ taskId: string, severity: string, description: { markdown: string }, stepsToReproduce?: { markdown: string }, assignedToIds?: string[], attachments?: Array<{ url: string, title?: string }> } | FormData} body
 *   - JSON when no files; FormData multipart (same pattern as blockers) when files attached.
 */
export const createProjectBug = (projectId, body) => {
    if (body instanceof FormData) {
        return http.post(`/projects/${projectId}/bugs`, body).then((res) => res.data?.data ?? res.data);
    }
    return http.post(`/projects/${projectId}/bugs`, body).then((res) => res.data?.data ?? res.data);
};

/**
 * PATCH /api/v1/projects/:projectId/bugs/:bugId
 * @param {{ status?: string }} body - e.g. { status: 'completed' }
 */
export const updateProjectBug = (projectId, bugId, body) =>
    http.patch(`/projects/${projectId}/bugs/${bugId}`, body).then((res) => res.data?.data ?? res.data);

/**
 * GET /api/v1/projects/:projectId/blockers
 */
export const getProjectBlockers = (projectId) =>
    http.get(`/projects/${projectId}/blockers`).then((res) => {
        const payload = res.data ?? {};
        const raw = payload.data ?? payload;
        if (Array.isArray(raw)) return raw;
        if (raw && Array.isArray(raw.blockers)) return raw.blockers;
        return [];
    });

/**
 * POST /api/v1/projects/:projectId/blockers
 * @param {{ title: string, description?: string | { markdown: string }, attachments?: Array<{ url: string, title?: string }> } | FormData} body
 *   - JSON when no files; FormData multipart (same as sprint/task) when files attached.
 */
export const createProjectBlocker = (projectId, body) => {
    if (body instanceof FormData) {
        return http.post(`/projects/${projectId}/blockers`, body).then((res) => res.data?.data ?? res.data);
    }
    return http.post(`/projects/${projectId}/blockers`, body).then((res) => res.data?.data ?? res.data);
};

/**
 * POST /api/v1/projects/:projectId/tasks/:taskId/blockers
 * Create a blocker for a specific task.
 *
 * @param {string} projectId
 * @param {string} taskId - task uuid (not sprintTask id)
 * @param {{ description: { markdown: string }, attachments?: Array<{ type: 'link' | 'file', url: string, title?: string }>, files?: File[] } | FormData} body
 *   - JSON when no files; FormData multipart when files attached.
 */
export const createProjectTaskBlocker = (projectId, taskId, body) => {
    if (body instanceof FormData) {
        return http
            .post(`/projects/${projectId}/tasks/${taskId}/blockers`, body)
            .then((res) => res.data?.data ?? res.data);
    }
    return http
        .post(`/projects/${projectId}/tasks/${taskId}/blockers`, body)
        .then((res) => res.data?.data ?? res.data);
};

/**
 * PATCH /api/v1/projects/:projectId/tasks/:taskId/blockers/:blockerId
 * Update a task blocker.
 *
 * @param {string} projectId
 * @param {string} taskId
 * @param {string} blockerId
 * @param {{ description?: { markdown: string }, attachments?: Array<{ type: 'link' | 'file', url: string, title?: string }>, files?: File[] } | FormData} body
 */
export const updateProjectTaskBlocker = (projectId, taskId, blockerId, body) => {
    if (body instanceof FormData) {
        return http
            .patch(`/projects/${projectId}/tasks/${taskId}/blockers/${blockerId}`, body)
            .then((res) => res.data?.data ?? res.data);
    }
    return http
        .patch(`/projects/${projectId}/tasks/${taskId}/blockers/${blockerId}`, body)
        .then((res) => res.data?.data ?? res.data);
};

/**
 * DELETE /api/v1/projects/:projectId/tasks/:taskId/blockers/:blockerId
 * @param {string} projectId
 * @param {string} taskId
 * @param {string} blockerId
 */
export const deleteProjectTaskBlocker = (projectId, taskId, blockerId) =>
    http
        .delete(`/projects/${projectId}/tasks/${taskId}/blockers/${blockerId}`)
        .then((res) => res.data?.data ?? res.data);

/**
 * PATCH /api/v1/projects/:projectId/blockers/:blockerId
 * @param {{ status?: string, resolved?: boolean }} body — e.g. { status: 'resolved' } | { status: 'open' }
 */
export const updateProjectBlocker = (projectId, blockerId, body) =>
    http.patch(`/projects/${projectId}/blockers/${blockerId}`, body).then((res) => res.data?.data ?? res.data);

/**
 * GET /api/v1/projects/:projectId/discussion
 * @returns {Promise<Array>} list of discussion posts (shape depends on API; normalized to array)
 */
export const getProjectDiscussions = (projectId) =>
    http.get(`/projects/${projectId}/discussion`).then((res) => {
        const payload = res.data ?? {};
        const raw = payload.data ?? payload;
        if (Array.isArray(raw)) return raw;
        if (raw && Array.isArray(raw.items)) return raw.items;
        if (raw && Array.isArray(raw.discussions)) return raw.discussions;
        if (raw && Array.isArray(raw.posts)) return raw.posts;
        return [];
    });

/**
 * POST /api/v1/projects/:projectId/discussion
 * @param {string} projectId
 * @param {{ content: string, attachments?: Array<{ url: string, title?: string }> } | FormData} body
 *   - JSON: { content, attachments? }. attachments for link-only.
 *   - FormData: when sending files use multipart with field "content" (text) and "files" (File[]).
 */
export const createProjectDiscussion = (projectId, body) => {
    if (body instanceof FormData) {
        return http.post(`/projects/${projectId}/discussion`, body).then((res) => res.data?.data ?? res.data);
    }
    return http.post(`/projects/${projectId}/discussion`, body).then((res) => res.data?.data ?? res.data);
};

/**
 * PATCH /api/v1/projects/:projectId/discussion/:discussionId
 * @param {string} projectId
 * @param {string} discussionId
 * @param {{ content: string }} body
 */
export const updateProjectDiscussion = (projectId, discussionId, body) =>
    http.patch(`/projects/${projectId}/discussion/${discussionId}`, body).then((res) => res.data?.data ?? res.data);

/**
 * DELETE /api/v1/projects/:projectId/discussion/:discussionId
 * @param {string} projectId
 * @param {string} discussionId
 */
export const deleteProjectDiscussion = (projectId, discussionId) =>
    http.delete(`/projects/${projectId}/discussion/${discussionId}`).then((res) => res.data?.data ?? res.data);


/**
 * GET /api/v1/projects/:projectId/bugs/:bugId/discussion
 * @returns {Promise<Array>} list of discussion posts (same shape as project discussion)
 */
export const getProjectBugDiscussions = (projectId, bugId) =>
    http.get(`/projects/${projectId}/bugs/${bugId}/discussion`).then((res) => {
        const payload = res.data ?? {};
        const raw = payload.data ?? payload;
        if (Array.isArray(raw)) return raw;
        if (raw && Array.isArray(raw.items)) return raw.items;
        if (raw && Array.isArray(raw.discussions)) return raw.discussions;
        if (raw && Array.isArray(raw.posts)) return raw.posts;
        return [];
    });

/**
 * POST /api/v1/projects/:projectId/bugs/:bugId/discussion
 * @param {string} projectId
 * @param {string} bugId
 * @param {{ content: string, attachments?: Array<{ url: string, title?: string }> } | FormData} body
 *   - JSON: { content, attachments? } when no files.
 *   - FormData: content, attachmentUrls (JSON string), files when attaching files.
 */
export const createProjectBugDiscussion = (projectId, bugId, body) => {
    if (body instanceof FormData) {
        return http.post(`/projects/${projectId}/bugs/${bugId}/discussion`, body).then((res) => res.data?.data ?? res.data);
    }
    return http.post(`/projects/${projectId}/bugs/${bugId}/discussion`, body).then((res) => res.data?.data ?? res.data);
};

/**
 * PATCH /api/v1/projects/:projectId/bugs/:bugId/discussion/:discussionId
 */
export const updateProjectBugDiscussion = (projectId, bugId, discussionId, body) =>
    http.patch(`/projects/${projectId}/bugs/${bugId}/discussion/${discussionId}`, body).then((res) => res.data?.data ?? res.data);

/**
 * DELETE /api/v1/projects/:projectId/bugs/:bugId/discussion/:discussionId
 */
export const deleteProjectBugDiscussion = (projectId, bugId, discussionId) =>
    http.delete(`/projects/${projectId}/bugs/${bugId}/discussion/${discussionId}`).then((res) => res.data?.data ?? res.data);

/**
 * GET /api/v1/projects/:projectId/blockers/:blockerId/discussion
 * @returns {Promise<Array>} list of discussion posts (same shape as project discussion)
 */
export const getProjectBlockerDiscussions = (projectId, blockerId, taskId = null) => {
    const url = taskId 
        ? `/projects/${projectId}/tasks/${taskId}/blockers/${blockerId}/discussion`
        : `/projects/${projectId}/blockers/${blockerId}/discussion`;
    return http.get(url).then((res) => {
        const payload = res.data ?? {};
        const raw = payload.data ?? payload;
        if (Array.isArray(raw)) return raw;
        if (raw && Array.isArray(raw.items)) return raw.items;
        if (raw && Array.isArray(raw.discussions)) return raw.discussions;
        if (raw && Array.isArray(raw.posts)) return raw.posts;
        return [];
    });
};

/**
 * POST /api/v1/projects/:projectId/blockers/:blockerId/discussion
 * @param {{ content: string, attachments?: Array<{ url: string, title?: string }> } | FormData} body
 *   - JSON: { content, attachments? } when no files.
 *   - FormData: content, attachmentUrls (JSON string), files when attaching files.
 */
export const createProjectBlockerDiscussion = (projectId, blockerId, body, taskId = null) => {
    const url = taskId 
        ? `/projects/${projectId}/tasks/${taskId}/blockers/${blockerId}/discussion`
        : `/projects/${projectId}/blockers/${blockerId}/discussion`;
    if (body instanceof FormData) {
        return http.post(url, body).then((res) => res.data?.data ?? res.data);
    }
    return http.post(url, body).then((res) => res.data?.data ?? res.data);
};

/**
 * PATCH /api/v1/projects/:projectId/blockers/:blockerId/discussion/:discussionId
 */
export const updateProjectBlockerDiscussion = (projectId, blockerId, discussionId, body, taskId = null) => {
    const url = taskId 
        ? `/projects/${projectId}/tasks/${taskId}/blockers/${blockerId}/discussion/${discussionId}`
        : `/projects/${projectId}/blockers/${blockerId}/discussion/${discussionId}`;
    return http.patch(url, body).then((res) => res.data?.data ?? res.data);
};

/**
 * DELETE /api/v1/projects/:projectId/blockers/:blockerId/discussion/:discussionId
 */
export const deleteProjectBlockerDiscussion = (projectId, blockerId, discussionId, taskId = null) => {
    const url = taskId 
        ? `/projects/${projectId}/tasks/${taskId}/blockers/${blockerId}/discussion/${discussionId}`
        : `/projects/${projectId}/blockers/${blockerId}/discussion/${discussionId}`;
    return http.delete(url).then((res) => res.data?.data ?? res.data);
};

/**
 * GET /api/v1/projects/:projectId/feedback
 * @returns {Promise<Array>} list of feedback items (normalized to array)
 */
export const getProjectFeedback = (projectId) =>
    http.get(`/projects/${projectId}/feedback`).then((res) => {
        const payload = res.data ?? {};
        const raw = payload.data ?? payload;
        if (Array.isArray(raw)) return raw;
        if (raw && Array.isArray(raw.items)) return raw.items;
        if (raw && Array.isArray(raw.feedback)) return raw.feedback;
        return [];
    });

/**
 * POST /api/v1/projects/:projectId/feedback
 * @param {{ content: string, rating: number }} body - content HTML from RichTextEditor, rating 1-5
 */
export const createProjectFeedback = (projectId, body) =>
    http.post(`/projects/${projectId}/feedback`, body).then((res) => res.data?.data ?? res.data);

/**
 * GET /api/v1/projects/:projectId/payments
 */
export const getProjectPayments = (projectId) =>
    http.get(`/projects/${projectId}/payments`).then((res) => {
        const payload = res.data ?? {};
        const raw = payload.data ?? payload;
        if (Array.isArray(raw)) return raw;
        if (raw && Array.isArray(raw.items)) return raw.items;
        if (raw && Array.isArray(raw.payments)) return raw.payments;
        return [];
    });

/**
 * POST /api/v1/projects/:projectId/payments
 * @param {{ amount: number, currency: string, notes?: string }} body
 */
export const createProjectPayment = (projectId, body) =>
    http.post(`/projects/${projectId}/payments`, body).then((res) => res.data?.data ?? res.data);

/**
 * POST /api/v1/projects/:projectId/contributors
 * @param {string} projectId - project uuid
 * @param {{ userIds: string[] }} body - array of user uuids
 */
export const addProjectContributors = (projectId, body) =>
    http.post(`/projects/${projectId}/contributors`, body).then((res) => res.data?.data ?? res.data);

/**
 * DELETE /api/v1/projects/:projectId/contributors/:userId
 * @param {string} projectId - project uuid
 * @param {string} userId - user uuid
 */
export const removeProjectContributor = (projectId, userId) =>
    http.delete(`/projects/${projectId}/contributors/${userId}`).then((res) => res.data?.data ?? res.data);

/**
 * GET /api/v1/projects/:projectId/qa/submissions
 * List QA submissions for the project (sprints/tasks submitted to QA).
 */
export const getProjectQASubmissions = (projectId) =>
    http.get(`/projects/${projectId}/qa/submissions`).then((res) => {
        const raw = res.data ?? {};
        const list = Array.isArray(raw.data) ? raw.data : [];
        return {
            data: list,
            total: raw.total ?? list.length,
            page: raw.page ?? 1,
            limit: raw.limit ?? 10,
            totalPages: raw.totalPages ?? 1,
        };
    });

/**
 * GET /api/v1/projects/:projectId/sprints/:sprintId/submissions/qa
 * List QA submissions for a specific sprint.
 */
export const getProjectSprintQASubmissions = (projectId, sprintId) =>
    http.get(`/projects/${projectId}/sprints/${sprintId}/submissions/qa`).then((res) => {
        // Response shape: { data: <single sprint object with tasks[] & qaSubmissions[]> }
        const raw = res.data ?? {};
        return { sprintData: raw.data ?? null };
    });

/**
 * GET /api/v1/projects/:projectId/milestones/:milestoneId/submissions/qa
 * List QA submissions for a specific milestone.
 */
export const getProjectMilestoneQASubmissions = (projectId, milestoneId) =>
    http.get(`/projects/${projectId}/milestones/${milestoneId}/submissions/qa`).then((res) => {
        // Response shape: { data: <object or array depending on backend> }
        const raw = res.data ?? {};
        if (Array.isArray(raw.data)) {
            return { data: raw.data, total: raw.total ?? raw.data.length };
        }
        return { sprintData: raw.data ?? null };
    });

/**
 * GET /api/v1/projects/:projectId/phases/:phaseId/submissions/qa
 * List QA submissions for a specific phase.
 */
export const getProjectPhaseQASubmissions = (projectId, phaseId) =>
    http.get(`/projects/${projectId}/phases/${phaseId}/submissions/qa`).then((res) => {
        // Response shape: { data: <object or array depending on backend> }
        const raw = res.data ?? {};
        if (Array.isArray(raw.data)) {
            return { data: raw.data, total: raw.total ?? raw.data.length };
        }
        return { sprintData: raw.data ?? null };
    });

/**
 * POST /api/v1/projects/:projectId/qa/submit
 * Submit selected sprints/tasks to QA or PMO.
 * When no files: sends JSON with notes and attachments (link-only).
 * When files present: sends multipart/form-data.
 *
 * @param {string} projectId
 * @param {{
 *   milestoneId?: string;
 *   submittedTo: 'qa' | 'pmo';
 *   sprintIds?: string[] | string;
 *   taskIds?: string[] | string;
 *   notes?: { markdown: string } | null;
 *   attachmentUrls?: { type: 'link'; url: string; title?: string }[];
 *   files?: File[] | FileList;
 * }} body
 */
export const submitProjectToQA = (projectId, body) => {
    const hasFiles =
        (Array.isArray(body?.files) && body.files.length > 0) ||
        (body?.files && typeof body.files.length === 'number' && body.files.length > 0);
    const linkAttachments =
        Array.isArray(body?.attachmentUrls) && body.attachmentUrls.length > 0
            ? body.attachmentUrls.filter((att) => att && att.url)
            : [];

    if (!hasFiles) {
        const jsonBody = {
            ...(body.milestoneId != null && { milestoneId: body.milestoneId }),
            ...(body.submittedTo && { submittedTo: body.submittedTo }),
            ...(body.sprintIds != null && {
                sprintIds: Array.isArray(body.sprintIds) ? body.sprintIds : [body.sprintIds],
            }),
            ...(body.taskIds != null && {
                taskIds: Array.isArray(body.taskIds) ? body.taskIds : [body.taskIds],
            }),
            ...(body.notes && typeof body.notes === 'object' && body.notes.markdown && { notes: body.notes }),
            ...(linkAttachments.length > 0 && { attachments: linkAttachments }),
        };
        return http.post(`/projects/${projectId}/qa/submit`, jsonBody).then((res) => res.data?.data ?? res.data);
    }

    const formData = new FormData();
    if (body.milestoneId) formData.append('milestoneId', String(body.milestoneId));
    if (body.submittedTo) formData.append('submittedTo', String(body.submittedTo));

    const sprintIds = Array.isArray(body.sprintIds) ? body.sprintIds : body.sprintIds ? [body.sprintIds] : [];
    sprintIds.forEach((id) => {
        if (id != null) formData.append('sprintIds[]', String(id));
    });

    const taskIds = Array.isArray(body.taskIds) ? body.taskIds : body.taskIds ? [body.taskIds] : [];
    taskIds.forEach((id) => {
        if (id != null) formData.append('taskIds[]', String(id));
    });

    if (body.notes && typeof body.notes === 'object' && body.notes.markdown) {
        formData.append('notes', JSON.stringify({ markdown: body.notes.markdown }));
    }

    if (linkAttachments.length > 0) {
        formData.append('attachmentUrls', JSON.stringify(linkAttachments));
    }

    if (hasFiles) {
        const filesArray = Array.isArray(body.files) ? body.files : Array.from(body.files);
        filesArray.forEach((file) => {
            if (file) formData.append('files', file);
        });
    }

    return http.post(projectQaSubmitPath(projectId), formData).then((res) => res.data?.data ?? res.data);
};

/**
 * POST /api/v1/projects/:projectId/tasks/:taskId/comments
 * Add a comment to a project task (QA feedback).
 * @param {string} projectId
 * @param {string} taskId - task uuid (taskId from task object)
 * @param {{ content: string }} body
 */
export const addProjectTaskComment = (projectId, taskId, body) =>
    http.post(`/projects/${projectId}/tasks/${taskId}/comments`, body).then((res) => res.data?.data ?? res.data);

/**
 * GET /api/v1/projects/:projectId/change-requests
 * @param {string} projectId
 * @param {{ page?: number, limit?: number }} params
 * @returns {{ data: array, total: number, page: number, limit: number, totalPages: number }}
 */
export const getProjectChangeRequests = (projectId, params = {}) =>
    http.get(`/projects/${projectId}/change-requests`, { params }).then((res) => {
        const raw = res.data ?? {};
        const list = Array.isArray(raw.data) ? raw.data : (Array.isArray(raw.changeRequests) ? raw.changeRequests : []);
        return {
            data: list,
            total: raw.total ?? list.length,
            page: raw.page ?? 1,
            limit: raw.limit ?? 20,
            totalPages: raw.totalPages ?? 1,
        };
    });

/**
 * GET /api/v1/projects/:projectId/pmo/submissions
 * List PMO submissions (completion requests) for the project with pagination. Use for non-client (e.g. PMO).
 * @param {string} projectId
 * @param {{ page?: number, limit?: number }} params
 * @returns {{ data: array, total: number, page: number, limit: number, totalPages: number }}
 */
export const getProjectPMOSubmissions = (projectId, params = {}) =>
    http.get(`/projects/${projectId}/pmo/submissions`, { params }).then((res) => {
        const raw = res.data ?? {};
        const list = Array.isArray(raw.data) ? raw.data : [];
        return {
            data: list,
            total: raw.total ?? list.length,
            page: raw.page ?? 1,
            limit: raw.limit ?? 10,
            totalPages: raw.totalPages ?? 1,
        };
    });

/**
 * GET /api/v1/projects/:projectId/completion-request
 * List completion requests for the project (client view). Use when current user is client.
 * @param {string} projectId
 * @param {{ page?: number, limit?: number }} params
 * @returns {{ data: array, total: number, page: number, limit: number, totalPages: number }}
 */
export const getProjectCompletionRequests = (projectId, params = {}) =>
    http.get(`/projects/${projectId}/completion-request`, { params }).then((res) => {
        const raw = res.data ?? {};
        const list = Array.isArray(raw.data) ? raw.data : [];
        return {
            data: list,
            total: raw.total ?? list.length,
            page: raw.page ?? 1,
            limit: raw.limit ?? 10,
            totalPages: raw.totalPages ?? 1,
        };
    });

/**
 * GET /api/v1/projects/:projectId/backlogs
 * List backlog items for the project (client rejections/backlog requests).
 * @param {string} projectId
 * @param {{ page?: number, limit?: number }} params
 * @returns {{ data: array, total: number, page: number, limit: number, totalPages: number }}
 */
export const getProjectBacklogs = (projectId, params = {}) =>
    http.get(`/projects/${projectId}/backlogs`, { params }).then((res) => {
        const raw = res.data ?? {};
        const list = Array.isArray(raw.data) ? raw.data : [];
        return {
            data: list,
            total: raw.total ?? list.length,
            page: raw.page ?? 1,
            limit: raw.limit ?? 10,
            totalPages: raw.totalPages ?? 1,
        };
    });

/**
 * POST /api/v1/projects/:projectId/backlogs/promote
 * Re-submit selected sprint tasks from a backlog context for client review.
 * @param {string} projectId
 * @param {{ sprintTaskIds: string[] }} body
 */
export const promoteProjectBacklogs = (projectId, body) =>
    http.post(`/projects/${projectId}/backlogs/promote`, body).then((res) => res.data?.data ?? res.data);

/**
 * POST /api/v1/projects/:projectId/completion-request
 * Create completion request (submit to client). JSON when no files; multipart when files present.
 *
 * @param {string} projectId
 * @param {{
 *   milestoneId?: string;
 *   submissionId?: string;
 *   notes?: { markdown?: string } | null;
 *   attachments?: Array<{ type: 'link'; url: string; title?: string }>;
 *   attachmentUrls?: Array<{ type: 'link'; url: string; title?: string }>;
 *   files?: File[] | FileList;
 * }} body
 *   - JSON / multipart: include milestoneId and/or submissionId only when provided (PMO submit uses submissionId only).
 */
export const createCompletionRequest = (projectId, body) => {
    const hasFiles =
        (Array.isArray(body?.files) && body.files.length > 0) ||
        (body?.files && typeof body.files.length === 'number' && body.files.length > 0);
    const linkList = Array.isArray(body?.attachments) ? body.attachments : Array.isArray(body?.attachmentUrls) ? body.attachmentUrls : [];
    const linkAttachments = linkList.filter((att) => att && att.url);

    if (!hasFiles) {
        const jsonBody = {
            ...(body.milestoneId != null && body.milestoneId !== '' && { milestoneId: String(body.milestoneId) }),
            ...(body.submissionId != null && body.submissionId !== '' && { submissionId: String(body.submissionId) }),
            ...(body.notes && typeof body.notes.markdown === 'string' && { notes: { markdown: body.notes.markdown } }),
            ...(linkAttachments.length > 0 && { attachments: linkAttachments }),
        };
        return http
            .post(`/projects/${projectId}/completion-request`, jsonBody)
            .then((res) => res.data?.data ?? res.data);
    }

    const formData = new FormData();
    if (body.milestoneId != null && body.milestoneId !== '') {
        formData.append('milestoneId', String(body.milestoneId));
    }
    if (body.submissionId != null && body.submissionId !== '') {
        formData.append('submissionId', String(body.submissionId));
    }
    const notes = body?.notes;
    if (notes != null) {
        // Backend expects multipart field `notes` (string markdown OR JSON string like {"markdown":"..."}).
        if (typeof notes === 'string') {
            formData.append('notes', notes);
        } else if (typeof notes === 'object' && typeof notes.markdown === 'string') {
            formData.append('notes', JSON.stringify({ markdown: notes.markdown }));
        }
    }
    if (linkAttachments.length > 0) {
        formData.append('attachmentUrls', JSON.stringify(linkAttachments));
    }
    const filesArray = Array.isArray(body.files) ? body.files : Array.from(body.files ?? []);
    filesArray.forEach((file) => {
        if (file) formData.append('files', file);
    });
    return http
        .post(`/projects/${projectId}/completion-request`, formData)
        .then((res) => res.data?.data ?? res.data);
};

/**
 * PATCH /api/v1/projects/:projectId/completion-request/:requestId/respond
 * Client responds to a completion request (approve or reject). JSON when no files; multipart when files present.
 *
 * @param {string} projectId
 * @param {string} requestId - completion request id
 * @param {{
 *   action: 'accepted' | 'declined';
 *   rating?: number;
 *   note?: string;
 *   attachmentUrls?: Array<{ type: 'link'; url: string; title?: string }>;
 *   files?: File[] | FileList;
 * }} body
 *   - JSON: { action, rating?, note }
 *   - Multipart: action, note (markdown), attachmentUrls (JSON string), files (File[])
 */
export const respondToCompletionRequest = (projectId, requestId, body) => {
    const action =
        String(body?.action ?? '').toLowerCase().trim() === 'declined' ? 'declined' : 'accepted';
    const hasFiles =
        (Array.isArray(body?.files) && body.files.length > 0) ||
        (body?.files && typeof body.files.length === 'number' && body.files.length > 0);
    const linkList = Array.isArray(body?.attachmentUrls) ? body.attachmentUrls : [];
    const linkAttachments = linkList.filter((att) => att && att.url);

    if (!hasFiles && linkAttachments.length === 0) {
        const jsonBody = {
            action,
            ...(typeof body.rating === 'number' && { rating: body.rating }),
            ...(typeof body.note === 'string' && body.note.trim() && { note: body.note.trim() }),
        };
        return http
            .patch(`/projects/${projectId}/completion-request/${requestId}/respond`, jsonBody)
            .then((res) => res.data?.data ?? res.data);
    }

    const formData = new FormData();
    formData.append('action', action);
    formData.append('note', typeof body.note === 'string' ? body.note : '');
    if (typeof body.rating === 'number') {
        formData.append('rating', String(body.rating));
    }
    if (linkAttachments.length > 0) {
        formData.append('attachmentUrls', JSON.stringify(linkAttachments));
    }
    const filesArray = Array.isArray(body.files) ? body.files : Array.from(body.files ?? []);
    filesArray.forEach((file) => {
        if (file) formData.append('file', file);
    });
    return http
        .patch(`/projects/${projectId}/completion-request/${requestId}/respond`, formData)
        .then((res) => res.data?.data ?? res.data);
};

/**
 * POST /api/v1/projects/:projectId/pmo/submissions/:submissionId/submit-to-client
 * Submit one PMO submission to the client (scoped by submission id — not whole milestone).
 * JSON when no files; multipart when files present (same shape as createCompletionRequest body, without milestoneId).
 *
 * @param {string} projectId
 * @param {string} submissionId
 * @param {{
 *   notes?: { markdown?: string } | null;
 *   attachments?: Array<{ type: 'link'; url: string; title?: string }>;
 *   attachmentUrls?: Array<{ type: 'link'; url: string; title?: string }>;
 *   files?: File[] | FileList;
 * }} [body]
 */
export const submitPMOSubmissionToClient = (projectId, submissionId, body = {}) => {
    const hasFiles =
        (Array.isArray(body?.files) && body.files.length > 0) ||
        (body?.files && typeof body.files.length === 'number' && body.files.length > 0);
    const linkList = Array.isArray(body?.attachments)
        ? body.attachments
        : Array.isArray(body?.attachmentUrls)
            ? body.attachmentUrls
            : [];
    const linkAttachments = linkList.filter((att) => att && att.url);

    const url = `/projects/${projectId}/pmo/submissions/${submissionId}/submit-to-client`;

    if (!hasFiles) {
        const jsonBody = {
            ...(body.notes && typeof body.notes.markdown === 'string' && { notes: { markdown: body.notes.markdown } }),
            ...(linkAttachments.length > 0 && { attachments: linkAttachments }),
        };
        return http.post(url, jsonBody).then((res) => res.data?.data ?? res.data);
    }

    const formData = new FormData();
    const notes = body?.notes;
    if (notes != null) {
        if (typeof notes === 'string') {
            formData.append('notes', notes);
        } else if (typeof notes === 'object' && typeof notes.markdown === 'string') {
            formData.append('notes', JSON.stringify({ markdown: notes.markdown }));
        }
    }
    if (linkAttachments.length > 0) {
        formData.append('attachmentUrls', JSON.stringify(linkAttachments));
    }
    const filesArray = Array.isArray(body.files) ? body.files : Array.from(body.files ?? []);
    filesArray.forEach((file) => {
        if (file) formData.append('files', file);
    });
    return http.post(url, formData).then((res) => res.data?.data ?? res.data);
};

/**
 * POST /api/v1/projects/:projectId/change-requests
 * @param {string} projectId
 * @param {{
 *  title?: string,
 *  description?: string | { markdown?: string, links?: Array<{ type: 'link'; url: string; title?: string }> },
 *  submissionId?: string,
 *  completionRequestId?: string,
 *  attachments?: Array<{ type: 'link' | 'file'; url: string; title?: string }>,
 *  attachmentUrls?: Array<{ type: 'link'; url: string; title?: string }>, // legacy alias
 *  files?: File[] | FileList
 * }} body
 */
export const createProjectChangeRequest = (projectId, body) => {
    const hasFiles =
        (Array.isArray(body?.files) && body.files.length > 0) ||
        (body?.files && typeof body.files.length === 'number' && body.files.length > 0);
    const linkList = Array.isArray(body?.attachments)
        ? body.attachments
        : Array.isArray(body?.attachmentUrls)
            ? body.attachmentUrls
            : [];
    const linkAttachments = linkList.filter((att) => att && att.url);

    const rawTitle = String(body?.title ?? '').trim();
    const rawDescription = body?.description ?? '';
    const descriptionObj =
        rawDescription && typeof rawDescription === 'object'
            ? rawDescription
            : {
                markdown: String(rawDescription ?? '').trim(),
            };
    const mergedMarkdown = [rawTitle, String(descriptionObj?.markdown ?? '').trim()].filter(Boolean).join('\n\n');
    const payloadDescription =
        linkAttachments.length > 0
            ? { ...descriptionObj, markdown: mergedMarkdown, links: linkAttachments }
            : { ...descriptionObj, markdown: mergedMarkdown };

    // Backend accepts JSON whenever no files are uploaded (even with link attachments).
    if (!hasFiles) {
        const jsonBody = {
            ...(body?.submissionId != null && { submissionId: String(body.submissionId) }),
            ...(body?.completionRequestId != null && { completionRequestId: String(body.completionRequestId) }),
            ...(mergedMarkdown || linkAttachments.length > 0 ? { description: payloadDescription } : {}),
            ...(linkAttachments.length > 0 ? { attachments: linkAttachments } : {}),
        };
        return http
            .post(`/projects/${projectId}/change-requests`, jsonBody)
            .then((res) => res.data?.data ?? res.data);
    }

    const formData = new FormData();
    if (body?.submissionId != null) {
        formData.append('submissionId', String(body.submissionId));
    }
    if (mergedMarkdown || linkAttachments.length > 0) {
        formData.append('description', JSON.stringify(payloadDescription));
    }
    if (body?.completionRequestId != null) {
        formData.append('completionRequestId', String(body.completionRequestId));
    }
    if (linkAttachments.length > 0) {
        formData.append('attachmentUrls', JSON.stringify(linkAttachments));
    }
    const filesArray = Array.isArray(body.files) ? body.files : Array.from(body.files ?? []);
    filesArray.forEach((file) => {
        if (file) formData.append('files', file);
    });
    return http.post(`/projects/${projectId}/change-requests`, formData).then((res) => res.data?.data ?? res.data);
};

/**
 * PATCH /api/v1/projects/:projectId/change-requests/:requestId/resolve
 * @param {{ status: string }} body - e.g. { status: 'approved' }
 */
export const updateProjectChangeRequest = (projectId, requestId, body) =>
    http.patch(`/projects/${projectId}/change-requests/${requestId}/resolve`, body).then((res) => res.data?.data ?? res.data);

/**
 * GET /api/v1/projects/:projectId/collaboration
 * List project collaborations (e.g. invited companies, individuals).
 */
export const getProjectCollaboration = (projectId) =>
    http.get(`/projects/${projectId}/collaboration`).then((res) => {
        const raw = res.data?.data ?? res.data;
        if (Array.isArray(raw)) return raw;
        if (raw && Array.isArray(raw.data)) return raw.data;
        return raw?.collaborations ?? [];
    });

/**
 * POST /api/v1/projects/:projectId/collaboration/invite
 * @param {string} projectId
 * @param {{ tenantId?: string, companyId?: string }} body
 */
export const inviteProjectCollaboration = (projectId, body) =>
    http.post(`/projects/${projectId}/collaboration/invite`, body).then((res) => res.data?.data ?? res.data);

/**
 * POST /api/v1/projects/:projectId/collaboration/members
 * @param {string} projectId
 * @param {{ userId: string }} body
 */
export const inviteProjectCollaborationMember = (projectId, body) =>
    http.post(`/projects/${projectId}/collaboration/members`, body).then((res) => res.data?.data ?? res.data);

/**
 * POST /api/v1/projects/:projectId/collaboration/accept
 * @param {string} projectId
 * @param {{ collaborationId?: string }} [body]
 */
export const acceptProjectCollaboration = (projectId, body = {}) =>
    http.post(`/projects/${projectId}/collaboration/accept`, body).then((res) => res.data?.data ?? res.data);

/**
 * POST /api/v1/projects/:projectId/collaboration/reject
 * @param {string} projectId
 * @param {{ collaborationId?: string }} [body]
 */
export const rejectProjectCollaboration = (projectId, body = {}) =>
    http.post(`/projects/${projectId}/collaboration/reject`, body).then((res) => res.data?.data ?? res.data);

/**
 * GET /api/v1/projects/:projectId/issues-and-upgrades
 * @param {string} projectId
 * @param {{ page?: number, limit?: number }} params
 * @returns {{ data: array, total: number, page: number, limit: number, totalPages: number }}
 */
export const getProjectIssuesAndUpgrades = (projectId, params = {}) =>
    http.get(`/projects/${projectId}/issues-and-upgrades`, { params }).then((res) => {
        const raw = res.data ?? {};
        const list = Array.isArray(raw.data)
            ? raw.data
            : Array.isArray(raw.items)
                ? raw.items
                : Array.isArray(raw.issuesAndUpgrades)
                    ? raw.issuesAndUpgrades
                    : [];
        return {
            data: list,
            total: raw.total ?? list.length,
            page: raw.page ?? 1,
            limit: raw.limit ?? 10,
            totalPages: raw.totalPages ?? 1,
        };
    });

/**
 * GET /api/v1/projects/:projectId/issues-and-upgrades/:issueOrUpgradeId
 */
export const getProjectIssueOrUpgradeById = (projectId, issueOrUpgradeId) =>
    http
        .get(`/projects/${projectId}/issues-and-upgrades/${issueOrUpgradeId}`)
        .then((res) => res.data?.data ?? res.data);

/**
 * POST /api/v1/projects/:projectId/issues-and-upgrades
 * JSON when no files, multipart when files are attached.
 */
export const createProjectIssueOrUpgrade = (projectId, body) => {
    if (body instanceof FormData) {
        return http.post(`/projects/${projectId}/issues-and-upgrades`, body).then((res) => res.data?.data ?? res.data);
    }
    return http.post(`/projects/${projectId}/issues-and-upgrades`, body).then((res) => res.data?.data ?? res.data);
};

/**
 * PATCH /api/v1/projects/:projectId/issues-and-upgrades/:issueOrUpgradeId
 */
export const updateProjectIssueOrUpgrade = (projectId, issueOrUpgradeId, body) =>
    http
        .patch(`/projects/${projectId}/issues-and-upgrades/${issueOrUpgradeId}`, body)
        .then((res) => res.data?.data ?? res.data);

/**
 * PATCH /api/v1/projects/:projectId/issues-and-upgrades/:issueOrUpgradeId/submit
 */
export const submitProjectIssueOrUpgrade = (projectId, issueOrUpgradeId, body) =>
    http
        .patch(`/projects/${projectId}/issues-and-upgrades/${issueOrUpgradeId}/submit`, body ?? {})
        .then((res) => res.data?.data ?? res.data);

/**
 * PATCH /api/v1/projects/:projectId/issues-and-upgrades/:issueOrUpgradeId/resolve
 */
export const resolveProjectIssueOrUpgrade = (projectId, issueOrUpgradeId) =>
    http
        .patch(`/projects/${projectId}/issues-and-upgrades/${issueOrUpgradeId}/resolve`)
        .then((res) => res.data?.data ?? res.data);

/**
 * DELETE /api/v1/projects/:projectId/issues-and-upgrades/:issueOrUpgradeId
 */
export const deleteProjectIssueOrUpgrade = (projectId, issueOrUpgradeId) =>
    http.delete(`/projects/${projectId}/issues-and-upgrades/${issueOrUpgradeId}`).then((res) => res.data?.data ?? res.data);

/**
 * POST /api/v1/projects/:projectId/issues-and-upgrades/:issueOrUpgradeId/tasks
 * @param {string} projectId
 * @param {string} issueOrUpgradeId
 * @param {{ sprintId: string, title: string, description?: object, type?: string, priority?: string, attachments?: Array<{ url: string, title?: string }> } | FormData} body
 *   - JSON when no files; multipart (Multer fields like sprint task create) when `files` are sent as FormData.
 */
export const createProjectIssueOrUpgradeTask = (projectId, issueOrUpgradeId, body) => {
    if (body instanceof FormData) {
        return http
            .post(`/projects/${projectId}/issues-and-upgrades/${issueOrUpgradeId}/tasks`, body)
            .then((res) => res.data?.data ?? res.data);
    }
    return http
        .post(`/projects/${projectId}/issues-and-upgrades/${issueOrUpgradeId}/tasks`, body)
        .then((res) => res.data?.data ?? res.data);
};

/**
 * GET /api/v1/projects/:projectId/issues-and-upgrades/:issueOrUpgradeId/tasks
 * Linked sprint tasks created from this issue/upgrade (optional API; callers should handle empty/errors).
 */
export const getProjectIssueOrUpgradeTasks = (projectId, issueOrUpgradeId) =>
    http.get(`/projects/${projectId}/issues-and-upgrades/${issueOrUpgradeId}/tasks`).then((res) => {
        const payload = res.data ?? {};
        const raw = payload.data ?? payload;
        if (Array.isArray(raw)) return raw;
        if (raw && Array.isArray(raw.tasks)) return raw.tasks;
        return [];
    });

/**
 * GET /api/v1/projects/:projectId/client-tickets
 * @param {string} projectId
 * @param {{ page?: number, limit?: number }} params
 * @returns {{ items: array, total: number, page: number, limit: number, totalPages: number }}
 */
export const getProjectClientTickets = (projectId, params = {}) =>
    http.get(`/projects/${projectId}/client-tickets`, { params }).then((res) => {
        const raw = res.data ?? {};
        const list = Array.isArray(raw.items)
            ? raw.items
            : Array.isArray(raw.data)
                ? raw.data
                : Array.isArray(raw.clientTickets)
                    ? raw.clientTickets
                    : [];
        return {
            items: list,
            total: raw.total ?? list.length,
            page: raw.page ?? 1,
            limit: raw.limit ?? 10,
            totalPages: raw.totalPages ?? 1,
        };
    });

/**
 * POST /api/v1/projects/:projectId/client-tickets
 * JSON when no files, multipart when files are attached.
 */
export const createProjectClientTicket = (projectId, body) => {
    if (body instanceof FormData) {
        return http.post(`/projects/${projectId}/client-tickets`, body).then((res) => res.data?.data ?? res.data);
    }
    return http.post(`/projects/${projectId}/client-tickets`, body).then((res) => res.data?.data ?? res.data);
};

/**
 * PATCH /api/v1/projects/:projectId/client-tickets/:clientTicketId
 */
export const updateProjectClientTicket = (projectId, clientTicketId, body) =>
    http
        .patch(`/projects/${projectId}/client-tickets/${clientTicketId}`, body)
        .then((res) => res.data?.data ?? res.data);

/**
 * PATCH /api/v1/projects/:projectId/client-tickets/:clientTicketId/acknowledge
 */
export const acknowledgeProjectClientTicket = (projectId, clientTicketId) =>
    http
        .patch(`/projects/${projectId}/client-tickets/${clientTicketId}/acknowledge`)
        .then((res) => res.data?.data ?? res.data);

/**
 * PATCH /api/v1/projects/:projectId/client-tickets/:clientTicketId/submit
 */
export const submitProjectClientTicket = (projectId, clientTicketId, body) =>
    http
        .patch(`/projects/${projectId}/client-tickets/${clientTicketId}/submit`, body)
        .then((res) => res.data?.data ?? res.data);

/**
 * PATCH /api/v1/projects/:projectId/client-tickets/:clientTicketId/submit-to-client
 */
export const submitProjectClientTicketToClient = (projectId, clientTicketId) =>
    http
        .patch(`/projects/${projectId}/client-tickets/${clientTicketId}/submit-to-client`)
        .then((res) => res.data?.data ?? res.data);

/**
 * GET /api/v1/projects/:projectId/settings
 */
export const getProjectSettings = (projectId) =>
    http.get(`/projects/${projectId}/settings`).then((res) => res.data?.data ?? res.data);

/**
 * PATCH /api/v1/projects/:projectId/settings
 */
export const updateProjectSettings = (projectId, body) =>
    http.patch(`/projects/${projectId}/settings`, body).then((res) => res.data?.data ?? res.data);

/**
 * DELETE /api/v1/projects/:projectId
 * Permanently deletes the project.
 * @param {string} projectId
 */
export const deleteProject = (projectId) =>
    http.delete(`/projects/${projectId}`).then((res) => res.data?.data ?? res.data);

/**
 * GET /api/v1/projects/:projectId/submission-memos
 * @param {string} projectId
 * @param {{ page?: number, limit?: number }} params
 */
export const getProjectSubmissionMemos = (projectId, params = {}) =>
    http.get(`/projects/${projectId}/submission-memos`, { params }).then((res) => {
        const raw = res.data ?? {};
        const list = Array.isArray(raw.data) ? raw.data : (Array.isArray(raw.items) ? raw.items : (Array.isArray(raw.submissionMemos) ? raw.submissionMemos : []));
        return {
            data: list,
            total: raw.total ?? list.length,
            page: raw.page ?? 1,
            limit: raw.limit ?? 20,
            totalPages: raw.totalPages ?? 1,
        };
    });

/**
 * POST /api/v1/projects/:projectId/submission-memos
 * @param {string} projectId
 * @param {object | FormData} body
 */
export const createProjectSubmissionMemo = (projectId, body) => {
    if (body instanceof FormData) {
        return http.post(`/projects/${projectId}/submission-memos`, body).then((res) => res.data?.data ?? res.data);
    }
    return http.post(`/projects/${projectId}/submission-memos`, body).then((res) => res.data?.data ?? res.data);
};

/**
 * GET /api/v1/projects/:projectId/submission-memos/:memoId
 */
export const getProjectSubmissionMemo = (projectId, memoId) =>
    http.get(`/projects/${projectId}/submission-memos/${memoId}`).then((res) => res.data?.data ?? res.data);

/**
 * PATCH /api/v1/projects/:projectId/submission-memos/:memoId
 */
export const updateProjectSubmissionMemo = (projectId, memoId, body) => {
    if (body instanceof FormData) {
        return http.patch(`/projects/${projectId}/submission-memos/${memoId}`, body).then((res) => res.data?.data ?? res.data);
    }
    return http.patch(`/projects/${projectId}/submission-memos/${memoId}`, body).then((res) => res.data?.data ?? res.data);
};

/**
 * DELETE /api/v1/projects/:projectId/submission-memos/:memoId
 */
export const deleteProjectSubmissionMemo = (projectId, memoId) =>
    http.delete(`/projects/${projectId}/submission-memos/${memoId}`).then((res) => res.data?.data ?? res.data);

/**
 * GET /api/v1/projects/:projectId/time-logs
 */
export const getProjectTimeLogs = (projectId, params) =>
    http.get(`/projects/${projectId}/time-logs`, { params }).then((res) => {
        const payload = res.data ?? {};
        const raw = payload.data ?? payload;
        const items = Array.isArray(raw) ? raw : (raw.items || raw.records || []);
        const total = raw.total ?? payload.total ?? items.length;
        return { data: items, total };
    });

/**
 * POST /api/v1/projects/:projectId/time-logs
 */
export const createProjectTimeLog = (projectId, body) =>
    http.post(`/projects/${projectId}/time-logs`, body).then((res) => res.data?.data ?? res.data);

/**
 * GET /api/v1/projects/:projectId/time-logs/monthly-summary
 */
export const getProjectTimeLogsMonthlySummary = (projectId, params) =>
    http.get(`/projects/${projectId}/time-logs/monthly-summary`, { params }).then((res) => res.data?.data ?? res.data);

/**
 * PATCH /api/v1/projects/:projectId/time-logs/:timeLogId
 */
export const updateProjectTimeLog = (projectId, timeLogId, body) =>
    http.patch(`/projects/${projectId}/time-logs/${timeLogId}`, body).then((res) => res.data?.data ?? res.data);

/**
 * DELETE /api/v1/projects/:projectId/time-logs/:timeLogId
 */
export const deleteProjectTimeLog = (projectId, timeLogId) =>
    http.delete(`/projects/${projectId}/time-logs/${timeLogId}`).then((res) => res.data?.data ?? res.data);
